import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { MessageCircle } from "lucide-react";
import { useLocation } from "react-router-dom";

// Lazy load the chatbot widget for performance
const ChatbotWidget = lazy(() => import("./ChatbotWidget"));

// Pages where chatbot should be disabled
const DISABLED_PATHS = [
  "/privacy-policy",
  "/terms-of-service",
  "/admin",
];

// Blog paths pattern - chatbot disabled by default
const BLOG_PATH_PATTERN = /^\/blog\/.+/;

// Analytics tracking
const trackEvent = (eventName: string) => {
  if (typeof window !== "undefined" && (window as any).gtag) {
    (window as any).gtag("event", eventName);
  }
  console.log(`[Chatbot Analytics] ${eventName}`);
};

export default function ChatbotLauncher() {
  const [isOpen, setIsOpen] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();
  const [formActive, setFormActive] = useState(false);
  const [showTip, setShowTip] = useState(false);

  // Hide the launcher while a visitor is filling in any form field
  useEffect(() => {
    let blurTimer: ReturnType<typeof setTimeout>;
    const onIn = (e: FocusEvent) => {
      clearTimeout(blurTimer);
      const t = e.target as HTMLElement | null;
      if (t?.closest("form")) setFormActive(true);
    };
    const onOut = () => {
      blurTimer = setTimeout(() => {
        if (!document.activeElement?.closest("form")) setFormActive(false);
      }, 150);
    };
    document.addEventListener("focusin", onIn);
    document.addEventListener("focusout", onOut);
    return () => {
      clearTimeout(blurTimer);
      document.removeEventListener("focusin", onIn);
      document.removeEventListener("focusout", onOut);
    };
  }, []);

  // Show the tooltip once per visit, then collapse it automatically
  useEffect(() => {
    if (!isVisible) return;
    try {
      if (sessionStorage.getItem("chat_tip_shown")) return;
      sessionStorage.setItem("chat_tip_shown", "1");
    } catch { /* ignore */ }
    setShowTip(true);
    const t = setTimeout(() => setShowTip(false), 4000);
    return () => clearTimeout(t);
  }, [isVisible]);

  // Check if chatbot should be shown on current page
  const shouldShowOnPage = useCallback(() => {
    const path = location.pathname;
    
    // Check disabled paths
    if (DISABLED_PATHS.some((p) => path.startsWith(p))) {
      return false;
    }
    
    // Check blog post pages (individual posts, not /blog index)
    if (BLOG_PATH_PATTERN.test(path)) {
      return false;
    }
    
    return true;
  }, [location.pathname]);

  // Lazy loading triggers
  useEffect(() => {
    if (!shouldShowOnPage()) return;

    let timeoutId: ReturnType<typeof setTimeout>;
    let scrollHandler: () => void;
    let hasTriggered = false;

    const triggerLoad = () => {
      if (hasTriggered) return;
      hasTriggered = true;
      setShouldLoad(true);
      
      // Small delay before showing button for smooth animation
      setTimeout(() => setIsVisible(true), 100);
      
      // Cleanup
      clearTimeout(timeoutId);
      window.removeEventListener("scroll", scrollHandler);
    };

    // Trigger 1: 4 second delay
    timeoutId = setTimeout(triggerLoad, 4000);

    // Trigger 2: 25% scroll
    scrollHandler = () => {
      const scrollPercent = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      if (scrollPercent >= 25) {
        triggerLoad();
      }
    };
    window.addEventListener("scroll", scrollHandler, { passive: true });

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("scroll", scrollHandler);
    };
  }, [shouldShowOnPage]);

  // Don't render anything if not on valid page
  if (!shouldShowOnPage()) {
    return null;
  }

  // Don't render until lazy load triggers
  if (!shouldLoad) {
    return null;
  }

  const handleOpen = () => {
    setIsOpen(true);
    trackEvent("chatbot_open");
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <>
      {/* Floating chat button */}
      {!isOpen && !formActive && (
        <button
          onClick={handleOpen}
          className={`fixed bottom-24 md:bottom-6 right-3 md:right-4 z-40 w-11 h-11 md:w-14 md:h-14 bg-primary text-primary-foreground rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group ${
            isVisible ? "animate-in fade-in slide-in-from-bottom-4" : "opacity-0"
          }`}
          aria-label="Open chat"
          style={{ 
            // Ensure button doesn't cover mobile sticky CTA
            marginBottom: "env(safe-area-inset-bottom, 0px)"
          }}
        >
          <MessageCircle className="w-5 h-5 md:w-6 md:h-6" />

          {/* Tooltip on desktop: shown once, then collapses (still available on hover) */}
          <span
            className={`absolute right-full mr-3 px-3 py-1.5 bg-background text-foreground text-sm font-medium rounded-lg shadow-lg transition-opacity whitespace-nowrap hidden md:block border border-border pointer-events-none ${
              showTip ? "opacity-100" : "opacity-0 group-hover:opacity-100"
            }`}
          >
            Get a quick estimate
          </span>
        </button>
      )}

      {/* Chatbot widget */}
      {isOpen && (
        <Suspense fallback={
          <div className="chat-panel fixed right-4 z-50 w-[360px] max-w-[calc(100vw-32px)] h-[520px] bg-background rounded-2xl shadow-2xl border border-border flex items-center justify-center">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
              <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
              <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
          </div>
        }>
          <ChatbotWidget onClose={handleClose} />
        </Suspense>
      )}
    </>
  );
}
